import { useParams } from 'react-router';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';

import InternalErrorState from '@/features/app/InternalErrorState';
import { useGetTyreReadingsQuery } from '@/services/api';
import Header from './Header';
import TyresGrid from './TyresGrid';
import { formatSyncTooltip, getSyncInfoFromTyres, groupTyresByAxle } from './utils';

interface MainContentProps {
    data: ReturnType<typeof useGetTyreReadingsQuery>['data'];
    isLoading: boolean;
    error: unknown;
    notAvailable: boolean;
    refetch: () => void;
}

const MainContent = ({ data, isLoading, error, notAvailable, refetch }: MainContentProps) => {
    if (isLoading) {
        return <TyresSkeleton />;
    }
    if (notAvailable) {
        return null;
    }
    if (error) {
        return <InternalErrorState reloadTriggerFunction={refetch} />;
    }
    if (data) {
        return <TyresGrid data={data} />;
    }
    return null;
};

const TyresSkeleton = () => (
    <div className="display-flex flex-column gap-10 padding-10">
        <ContentLoader height={40} width="100%" />
        <ContentLoader height={60} width="100%" />
        <ContentLoader height={60} width="100%" />
        <ContentLoader height={60} width="100%" />
    </div>
);

const Wrapper = () => {
    const { assetId } = useParams<{ assetId: string }>();
    const { data, isLoading, error, refetch } = useGetTyreReadingsQuery({ assetId: assetId ?? '' }, { skip: !assetId });

    const notAvailable = !!data && data.tyres.length === 0;
    const axles = data ? groupTyresByAxle(data.tyres) : [];
    const syncInfo = data ? getSyncInfoFromTyres(data.tyres) : null;
    const isStale = syncInfo !== null;
    const staleTooltip = syncInfo ? formatSyncTooltip(syncInfo) : undefined;

    return (
        <>
            <Header isStale={isStale} notAvailable={notAvailable} isLoading={isLoading} staleTooltip={staleTooltip} />
            <MainContent
                data={data}
                isLoading={isLoading}
                error={error}
                notAvailable={notAvailable}
                refetch={refetch}
            />
        </>
    );
};

export default Wrapper;
